import { initComponent } from './core/init-component';
import { bindAll } from './utils/bind-all';
import { initCursorFollowClose } from './utils/cursor-follow-close';

initComponent('.js-pdp-info-drawer', (drawer) => {
  const header = document.querySelector(
    '.js-header'
  ) as HTMLElement | null;

  if (!window.Shopify?.designMode) {
    document.body.appendChild(drawer);
  }

  const drawerOverlay = drawer.querySelector<HTMLElement>(
    '.js-pdp-drawer-overlay'
  );

  const drawerPanel = drawer.querySelector<HTMLElement>(
    '.js-pdp-drawer-panel'
  );

  const drawerCloseButton = drawer.querySelector<HTMLElement>(
    '.js-pdp-drawer-close'
  );

  // Close icon that follows the cursor over the overlay (md+).
  const closeFollow = drawer.querySelector<HTMLElement>('.js-drawer-close-follow');
  const cursorClose = initCursorFollowClose(drawerOverlay ?? null, closeFollow);


  const setActiveTab = (
    tab: string
  ) => {
    tabButtons.forEach((button) => {
      button.classList.toggle(
        'active',
        button.dataset.tab === tab
      );
    });
  
    tabContents.forEach((content) => {
      content.classList.toggle(
        'hidden',
        content.dataset.tabContent !== tab
      );
    });
  };
  
  const openDrawer = (
    event?: Event
  ) => {
    const trigger =
      event?.currentTarget as HTMLElement | null;
  
    if (
      trigger?.classList.contains(
        'js-pdp-info-drawer-trigger--size-guide'
      )
    ) {
      setActiveTab('size-guide');
    }
  
    if (
      trigger?.classList.contains(
        'js-pdp-info-drawer-trigger--details'
      )
    ) {
      setActiveTab('details');
    }
  
    if (
      trigger?.classList.contains(
        'js-pdp-info-drawer-trigger--shipping'
      )
    ) {
      setActiveTab('shipping');
    }
  
    if (!drawerOverlay || !drawerPanel) return;
  
    header?.classList.add(
      'opacity-0',
      'pointer-events-none'
    );
  
    drawerOverlay.classList.remove(
      'hidden',
      'pointer-events-none'
    );
  
    drawerCloseButton?.classList.remove(
      'hidden'
    );
  
    requestAnimationFrame(() => {
      drawerOverlay.classList.remove(
        'opacity-0'
      );
  
      drawerPanel.classList.remove(
        'translate-x-full'
      );
  
      document.body.classList.add(
        'overflow-hidden'
      );
    });
  };

  const closeDrawer = () => {
    if (!drawerOverlay || !drawerPanel) return;

    header?.classList.remove(
      'opacity-0',
      'pointer-events-none'
    );

    drawerCloseButton?.classList.add('hidden');

    cursorClose.hide();

    drawerOverlay.classList.add('opacity-0');

    drawerPanel.classList.add('translate-x-full');

    document.body.classList.remove('overflow-hidden');

    setTimeout(() => {
      drawerOverlay.classList.add(
        'hidden',
        'pointer-events-none'
      );
    }, 400);
  };

  bindAll(
    document,
    '.js-pdp-info-drawer-trigger',
    'click',
    openDrawer
  );

  bindAll(
    drawer,
    '.js-pdp-drawer-close',
    'click',
    closeDrawer
  );

  drawerOverlay?.addEventListener(
    'click',
    closeDrawer
  );

  // Tabs

  const tabButtons = [
    ...drawer.querySelectorAll<HTMLButtonElement>(
      '.js-pdp-drawer-tab'
    )
  ];
  
  const tabContents = [
    ...drawer.querySelectorAll<HTMLElement>(
      '.js-pdp-drawer-tab-content'
    )
  ];
  
  bindAll(
    drawer,
    '.js-pdp-drawer-tab',
    'click',
    (event: Event) => {
      const button = event.currentTarget as HTMLButtonElement;
  
      const tab = button.dataset.tab;
  
      if (!tab) return;
  
      tabButtons.forEach((btn) => {
        const active = btn === button;
  
        btn.classList.toggle(
          'active',
          active
        );
      });
  
      tabContents.forEach((content) => {
        content.classList.toggle(
          'hidden',
          content.dataset.tabContent !== tab
        );
      });
    }
  );

  // Size guide table

  const sizeGuideTable = drawer.querySelector<HTMLElement>(
    '.js-size-guide-table'
  );
  
  const sizeGuideCells = [
    ...(sizeGuideTable?.querySelectorAll<HTMLTableCellElement>(
      'td'
    ) || [])
  ];
  
  sizeGuideCells.forEach((cell) => {
    cell.addEventListener('mouseenter', () => {
      const row = cell.parentElement;
  
      if (!row || !sizeGuideTable) return;
  
      const columnIndex = [
        ...row.children
      ].indexOf(cell);
  
      sizeGuideTable
        .querySelectorAll('td')
        .forEach((el) => {
          el.classList.remove(
            'bg-surface-white-15',
            'bg-grey-0',
            'text-text-invert'
          );
        });
  
      row.querySelectorAll('td').forEach((el) => {
        el.classList.add('bg-surface-white-15');
      });
  
      sizeGuideTable
        .querySelectorAll(
          `tr td:nth-child(${columnIndex + 1})`
        )
        .forEach((el) => {
          el.classList.add('bg-surface-white-15');
        });
  
      cell.classList.remove(
        'bg-surface-white-15'
      );
  
      cell.classList.add(
        'bg-grey-0',
        'text-text-invert'
      );
    });
  
    cell.addEventListener('mouseleave', () => {
      sizeGuideTable
        ?.querySelectorAll('td')
        .forEach((el) => {
          el.classList.remove(
            'bg-surface-white-15',
            'bg-grey-0',
            'text-text-invert'
          );
        });
    });
  });

  // Table conversion

  const sizeGuideUnitInputs = [
    ...drawer.querySelectorAll<HTMLInputElement>(
      'input[name="size-guide-unit"]'
    )
  ];
  
  const convertCmToInches = (
    value: number
  ) => {
    return (value / 2.54).toFixed(1);
  };
  
  const updateSizeGuideUnits = () => {
    const activeUnit =
      sizeGuideUnitInputs.find(
        (input) => input.checked
      )?.value || 'cm';
  
    sizeGuideCells.forEach((cell) => {
      if (cell.cellIndex === 0) {
        return;
      }

      if (!cell.dataset.originalValue) {
        cell.dataset.originalValue =
          cell.textContent?.trim() || '';
      }
  
      const originalValue =
        cell.dataset.originalValue;
  
      if (!originalValue) return;
  
      if (activeUnit === 'cm') {
        cell.textContent = originalValue;
        return;
      }
  
      cell.textContent = originalValue.replace(
        /\d+(\.\d+)?/g,
        (match) => {
          const number = Number(match);
  
          if (Number.isNaN(number)) {
            return match;
          }
  
          return convertCmToInches(number);
        }
      );
    });
  };
  
  sizeGuideUnitInputs.forEach((input) => {
    input.addEventListener(
      'change',
      updateSizeGuideUnits
    );
  });

  return {
    unload() {
      cursorClose.destroy();
    }
  };
});